import {
  ConstructGeneralViewStateParam,
  ExploreTabRestoreLocalSavedViewProps,
  ExploreViewState,
} from '@models/SavedViews';
import { useDispatch, useSelector } from 'react-redux';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { bindActionCreators } from 'redux';
import { actionCreators, actionCreatorsSideFilters } from '@state/Index';
import { FeatureOption, PageName, PageType, ShareType } from '@utils/pageConfig/Types';
import { useRestoreSavedView } from '@ecosystem/newron-client-lib';
import {
  checkIsSubscribed,
  constructGeneralViewStateProps,
  getGrowthType,
  handleCustomPeriodicityAndRestoreGeneralViewState,
  handleRestoreMultibannerChange,
} from './SavedViewHookUtils';
import { useRestoreMultibannerRetailerClient } from './UseRestoreMultibannerRetailerClient';
import { useRestoreProductMultibannerRetailerClient } from './UseRestoreProductMultibannerRetailerClient';
import { ExploreTabView } from '@components/tab-nav/TabConfig';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { SaleType } from '@models/Products';

const applyTrendsSettings = (
  viewState: ExploreViewState,
  setSelectedShareType: (value: ShareType) => void,
  setGrowthOption: (value: GrowthTypes) => void,
  setIsLineChartView: (value: boolean) => void,
  setSelectedSaleType: (value: SaleType) => void,
  setSelectedAbsoluteType: (value: ShareType) => void,
  setSortBy: (value: any) => void,
): void => {
  if (viewState.selectedShareType) {
    setSelectedShareType(viewState.selectedShareType);
  }
  setGrowthOption(viewState.mainFilter.selectedGrowthType);

  if (viewState.trendsIsLineChartView) {
    setIsLineChartView(viewState.trendsIsLineChartView);
  }

  if (viewState.trendsSelectedSaleType) {
    setSelectedSaleType(viewState.trendsSelectedSaleType);
  }
  if (viewState.selectedAbsoluteType) {
    setSelectedAbsoluteType(viewState.selectedAbsoluteType);
  }
  if (viewState.sortByOption) {
    setSortBy(viewState.sortByOption);
  }
};

const applySegmentSettings = (
  setSortBy: (value: any) => void,
  viewState: ExploreViewState,
  setSelectedShareType: (value: ShareType) => void,
  setGrowthOption: (value: GrowthTypes) => void,
  setSegmentByFeature: (value: FeatureOption) => void,
  setSegmentKpi: (value: any) => void,
): void => {
  if (viewState.selectedShareType) {
    setSelectedShareType(viewState.selectedShareType);
  }
  if (viewState.segmentByFeature) {
    setSegmentByFeature(viewState.segmentByFeature);
  }
  if (viewState.segmentKpi) {
    setSegmentKpi(viewState.segmentKpi);
  }
  if (viewState.sortByOption) {
    setSortBy(viewState.sortByOption);
  }
  setGrowthOption(viewState.mainFilter.selectedGrowthType);
};

export const useRestoreExploreSavedView = (
  exploreRestoreLocalSavedViewProps: ExploreTabRestoreLocalSavedViewProps,
): void => {
  const {
    productType,
    setIsSubscribed,
    setIsTableView,
    setSelectedCountries,
    setAppliedCountries,
    setFilters,
    setDefaultFilters,
    setCurrentSortingOption,
    setGrowthOption,
    setSelectedShareType,
    selectedLink,
    setCustomMonthsPeriodState,
    setCustomWeeksPeriodState,
    setFeature,
    setSelectedSaleType,
    setRelativePeriod,
    setTimeFrame,
    setIsLineChartView,
    setPeriod,
    setSelectedAbsoluteType,
    setSortingOption,
    setIsRestoreFromSavedView,
    setSegmentByFeature,
    setSegmentKpi,
  } = exploreRestoreLocalSavedViewProps;

  // generate object holding the general states to be passed in the function later on
  const generalSavedViewProps = constructGeneralViewStateProps(exploreRestoreLocalSavedViewProps);
  const pageName = PageName.EXPLORE;
  const pageType = PageType.EXPLORE;

  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();

  const mainFiltersState = useSelector((state: any) => state.mainFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);

  const { restoreMultibannerRetailerClient } = useRestoreMultibannerRetailerClient();
  const { restoreProductMultibannerRetailerClient } = useRestoreProductMultibannerRetailerClient();

  const applyExploreViewState = (viewState: ExploreViewState): void => {
    if (!viewState) {
      return;
    }

    // initial checks
    if (!checkIsSubscribed(viewState.retailerClientState, pagesConfig, productType)) {
      setIsSubscribed(false);
      return;
    }

    const params: ConstructGeneralViewStateParam = {
      viewState,
      pagesConfig,
      pageType,
      pageName,
      updateSideFilters,
      updateMainFilters,
      mainFiltersState,
      generalSavedViewProps,
    };

    // restore the general states that exist in all pages
    handleCustomPeriodicityAndRestoreGeneralViewState(
      viewState,
      params,
      setCustomMonthsPeriodState,
      setCustomWeeksPeriodState,
    );

    const dataViewType = viewState['dataViewType'];
    const categoryLeaderboardIsTableView = viewState['isTableView'];

    const { feature, leaderboardIsTableView, selectedShareType, growthType, sorting } = viewState;
    setIsRestoreFromSavedView(true);
    if (feature) {
      setFeature(feature);
    }
    if (viewState.sorting) {
      setSortingOption(viewState.sorting);
    }

    if (viewState.mainFilter) {
      setRelativePeriod(viewState.mainFilter?.comparisonPeriod);
      setTimeFrame(viewState.mainFilter?.timeFrame);
      setPeriod(viewState.mainFilter?.period);
    }

    switch (selectedLink?.value) {
      case ExploreTabView.Leaderboard:
        if (dataViewType === 'Table') {
          setIsTableView(true);
        } else {
          setIsTableView(categoryLeaderboardIsTableView ?? !!leaderboardIsTableView);
        }

        setGrowthOption(getGrowthType(growthType));
        setCurrentSortingOption(sorting);

        if (selectedShareType) {
          setSelectedShareType(selectedShareType);
        }

        break;

      case ExploreTabView.Trends: {
        applyTrendsSettings(
          viewState,
          setSelectedShareType,
          setGrowthOption,
          setIsLineChartView,
          setSelectedSaleType,
          setSelectedAbsoluteType,
          pagesConfig.setSortByOptionsPerformanceAnalysisStackedChart,
        );
        break;
      }
      case ExploreTabView.Segments: {
        applySegmentSettings(
          pagesConfig.setSortByOptionsPerformanceAnalysisStackedChart,
          viewState,
          setSelectedShareType,
          setGrowthOption,
          setSegmentByFeature,
          setSegmentKpi,
        );
        break;
      }
      default:
        break;
    }

    handleRestoreMultibannerChange({
      viewState,
      retailerClientState,
      pageName,
      pageType,
      pagesConfig,
      restoreMultibannerRetailerClient,
      restoreProductMultibannerRetailerClient,
      setFilters,
      setAppliedCountries,
      setSelectedCountries,
      setDefaultFilters,
    });
  };
  useRestoreSavedView(applyExploreViewState);
};
